import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdToggleOn,
  MdToggleOff,
  MdClose,
  MdUpload,
} from "react-icons/md";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

Modal.setAppElement("#root");

const ImageCard = ({ image, onEdit, onDelete, onToggle, isLoading }) => (
  <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-lg">
    <div className="aspect-w-16 aspect-h-9">
      <img
        src={adminApiService.getPopupImageUrl(image.image_url)}
        alt="Popup"
        className="h-full w-full object-cover"
      />
    </div>
    <div className="bg-black/0 group-hover:bg-black/40 absolute inset-0 flex items-center justify-center transition-all duration-300">
      <div className="flex translate-y-4 gap-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
        <button
          onClick={onEdit}
          className="rounded-full bg-white p-2.5 text-blue-600 shadow-md transition-colors hover:bg-blue-50"
          aria-label="Edit popup image"
        >
          <MdEdit size={20} />
        </button>
        <button
          onClick={onDelete}
          className="rounded-full bg-white p-2.5 text-red-600 shadow-md transition-colors hover:bg-red-50"
          aria-label="Delete popup image"
        >
          <MdDelete size={20} />
        </button>
        <button
          onClick={onToggle}
          disabled={isLoading}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
            image.is_active
              ? "bg-green-100 text-green-800 hover:bg-green-200"
              : "bg-red-100 text-red-800 hover:bg-red-200"
          } disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {image.is_active ? (
            <>
              <MdToggleOn size={16} />
              Active
            </>
          ) : (
            <>
              <MdToggleOff size={16} />
              Inactive
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

const FileUploadArea = ({ previewUrl, onFileChange, inputRef }) => (
  <div className="mt-1.5">
    <label
      htmlFor="fileInput"
      className="flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 transition-colors hover:bg-gray-100"
    >
      {previewUrl ? (
        <div className="relative w-full">
          <img
            src={previewUrl}
            alt="Preview"
            className="mx-auto h-48 w-auto rounded-lg object-contain"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 right-2 rounded-full bg-white p-2 shadow-md transition-colors hover:bg-gray-100"
          >
            <MdEdit size={20} className="text-gray-600" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center">
          <MdUpload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-blue-600">Click to upload</span>{" "}
            or drag and drop
          </p>
          <p className="mt-1 text-xs text-gray-500">
            PNG, JPG or JPEG up to 2MB
          </p>
        </div>
      )}
      <input
        id="fileInput"
        type="file"
        accept="image/*"
        onChange={onFileChange}
        ref={inputRef}
        className="hidden"
      />
    </label>
  </div>
);

const PopupSettings = () => {
  const [popupImages, setPopupImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    previewUrl: "",
    selectedImage: null,
    link: "",
  });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPopupSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminApiService.getPopupSettingsPublic();

      if (!response.ok) throw new Error("Failed to fetch popup settings");

      const { settings } = await response.json();
      console.log(settings);

      setPopupImages(settings || []);
    } catch (err) {
      toast.error("Failed to fetch popup settings");
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPopupSettings();
  }, [fetchPopupSettings]);

  const openModal = (image = null) => {
    setModalState({
      isOpen: true,
      previewUrl: image
        ? `${process.env.REACT_APP_API_URL_Plain}/config/uploads/popup_images/${image.image_url}`
        : "",
      selectedImage: image,
      link: image?.link || "",
    });
    setFile(null);
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      previewUrl: "",
      selectedImage: null,
      link: "",
    });
    setFile(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setModalState((prev) => ({
        ...prev,
        previewUrl: URL.createObjectURL(selectedFile),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file && !modalState.selectedImage) {
      return toast.error("Please select an image to upload.");
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append("popup_image", file);
      }
      if (modalState.selectedImage) {
        formData.append("id", modalState.selectedImage.id);
      }
      formData.append("link", modalState.link);

      const res = modalState.selectedImage
        ? await adminApiService.updatePopupSettings(formData)
        : await adminApiService.addPopupSettings(formData);

      if (!res.ok) throw new Error("Failed to update popup settings");

      const { message, settings } = await res.json();
      toast.success(message);

      // Update the state with the response data
      if (modalState.selectedImage) {
        setPopupImages((prevImages) =>
          prevImages.map((img) => (img.id === settings.id ? settings : img))
        );
      } else {
        setPopupImages((prevImages) => [...prevImages, settings]);
      }

      closeModal();
    } catch (error) {
      toast.error("Error updating popup settings.");
      console.error("Update error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    setIsLoading(true);
    try {
      const response = await adminApiService.deletePopupImage(imageId);

      if (!response.ok) throw new Error("Failed to delete image");

      const { message } = await response.json();
      toast.success(message);

      // Remove the deleted image from state
      setPopupImages((prevImages) =>
        prevImages.filter((img) => img.id !== imageId)
      );
    } catch (error) {
      toast.error("Failed to delete image");
      console.error("Delete error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (imageId, currentStatus) => {
    setIsLoading(true);
    try {
      const response = await adminApiService.togglePopupImage(
        imageId,
        !currentStatus
      );

      if (!response.ok) throw new Error("Failed to update status");

      const { message, settings } = await response.json();
      toast.success(message);

      // Update the state with the response data
      setPopupImages((prevImages) =>
        prevImages.map((img) => (img.id === settings.id ? settings : img))
      );
    } catch (error) {
      toast.error("Failed to update status");
      console.error("Toggle error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">
          Popup Settings
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage popup images displayed on your website
        </p>
      </header>

      <div className="mb-8 flex justify-end">
        <button
          onClick={() => openModal()}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-70"
        >
          <MdAdd size={20} />
          Add New Popup Image
        </button>
      </div>

      {isLoading && !popupImages.length ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {popupImages.map((image) => (
            <ImageCard
              key={image.id}
              image={image}
              onEdit={() => openModal(image)}
              onDelete={() => handleDelete(image.id)}
              onToggle={() => handleToggleStatus(image.id, image.is_active)}
              isLoading={isLoading}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={closeModal}
        contentLabel="Edit Popup Image"
        className="relative mx-auto max-w-lg rounded-xl bg-white p-6 shadow-2xl outline-none"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        closeTimeoutMS={200}
      >
        <button
          onClick={closeModal}
          className="absolute right-4 top-4 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close modal"
        >
          <MdClose size={24} />
        </button>

        <h2 className="mb-6 text-2xl font-semibold text-gray-900">
          {modalState.selectedImage
            ? "Edit Popup Image"
            : "Add New Popup Image"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {modalState.selectedImage
                ? "Update Popup Image (optional)"
                : "Upload Popup Image"}
            </label>
            <FileUploadArea
              previewUrl={modalState.previewUrl}
              onFileChange={handleFileChange}
              inputRef={fileInputRef}
            />
          </div>

          <div>
            <label
              htmlFor="link"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Popup Link (optional)
            </label>
            <input
              type="url"
              id="link"
              value={modalState.link}
              onChange={(e) =>
                setModalState((prev) => ({ ...prev, link: e.target.value }))
              }
              placeholder="https://example.com"
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Add a URL where users will be redirected when clicking the popup
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="border-t-transparent h-4 w-4 animate-spin rounded-full border-2 border-white"></div>
                  Updating...
                </div>
              ) : modalState.selectedImage ? (
                "Update Image"
              ) : (
                "Add Image"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PopupSettings;
